import React, { useState } from "react";
import { Payment, Visibility, VisibilityOff } from "@mui/icons-material";

const TransactionsTab = ({ loan }) => {
  const transactions = loan?.transactions || [];
  const [showReversed, setShowReversed] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  // Filter transactions based on showReversed toggle
  const filteredTransactions = showReversed
    ? transactions
    : transactions.filter(t => t.status !== 'reversed');

  // Format currency
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "$0.00";
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return "N/A";
    try {
      const d = new Date(date);
      return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return "N/A";
    }
  };

  // Get transaction type display
  const getTransactionTypeDisplay = (type) => {
    const typeMap = {
      'payment': 'Payment',
      'penaltyCharged': 'Penalty Charged',
      'payOff': 'Pay Off',
      'writeOff': 'Write Off'
    };
    return typeMap[type] || type;
  };

  // Get transaction color
  const getTransactionColor = (transaction) => {
    if (transaction.status === 'reversed') return 'text-gray-400';
    if (transaction.type === 'payment' || transaction.type === 'payOff') return 'text-green-600';
    if (transaction.type === 'penaltyCharged') return 'text-red-600';
    return 'text-gray-900';
  };

  if (!transactions || transactions.length === 0) {
    return (
      <div className="p-12 text-center">
        <Payment className="mx-auto text-gray-400 mb-4" style={{ fontSize: 64 }} />
        <h3 className="text-lg font-semibold text-gray-700 mb-2">No Transactions</h3>
        <p className="text-gray-500">
          No payment transactions have been recorded for this loan yet.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Controls */}
      <div className="mb-4 flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">
          Transaction History ({filteredTransactions.length} records)
        </h3>
        <button
          onClick={() => setShowReversed(!showReversed)}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          {showReversed ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
          {showReversed ? 'Hide Reversed' : 'Show Reversed'}
        </button>
      </div>

      {/* Transactions Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Principal
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Interest
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fee
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Penalty
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Final Balance
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Details
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredTransactions.map((transaction, index) => {
                const amount = transaction.credit || transaction.debit || 0;
                const isReversed = transaction.status === 'reversed';

                return (
                  <tr
                    key={transaction._id || index}
                    className={`${isReversed ? 'bg-gray-50 opacity-60' : 'hover:bg-gray-50'}`}
                  >
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(transaction.transactionDate || transaction.createdAt)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <div className={getTransactionColor(transaction)}>
                        <div className="font-medium">{getTransactionTypeDisplay(transaction.type)}</div>
                        {transaction.method && (
                          <div className="text-xs text-gray-500">{transaction.method}</div>
                        )}
                      </div>
                    </td>
                    <td className={`px-4 py-3 whitespace-nowrap text-sm text-right font-semibold ${getTransactionColor(transaction)}`}>
                      {formatCurrency(amount)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-right">
                      {formatCurrency(transaction.allocation?.principal || 0)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-right">
                      {formatCurrency(transaction.allocation?.interest || 0)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-right">
                      {formatCurrency(transaction.allocation?.fee || 0)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-right">
                      {formatCurrency(transaction.allocation?.penalty || 0)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                      {formatCurrency(transaction.finalLoanBalance || 0)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {isReversed ? (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                          Reversed
                        </span>
                      ) : (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          Completed
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <button
                        onClick={() => setSelectedTransaction(transaction)}
                        className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Transaction Detail Modal */}
      {selectedTransaction && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl">
            <div className="border-b p-6">
              <h2 className="text-xl font-bold text-gray-900">Transaction Details</h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Transaction Date</p>
                  <p className="text-base font-semibold text-gray-900">
                    {formatDate(selectedTransaction.transactionDate || selectedTransaction.createdAt)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Type</p>
                  <p className="text-base font-semibold text-gray-900">
                    {getTransactionTypeDisplay(selectedTransaction.type)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Method</p>
                  <p className="text-base font-semibold text-gray-900">
                    {selectedTransaction.method || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Amount</p>
                  <p className="text-base font-semibold text-gray-900">
                    {formatCurrency(selectedTransaction.credit || selectedTransaction.debit || 0)}
                  </p>
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="text-sm font-bold text-gray-900 mb-3">Allocation Breakdown</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Principal</p>
                    <p className="text-base font-semibold text-gray-900">
                      {formatCurrency(selectedTransaction.allocation?.principal || 0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Interest</p>
                    <p className="text-base font-semibold text-gray-900">
                      {formatCurrency(selectedTransaction.allocation?.interest || 0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Fee</p>
                    <p className="text-base font-semibold text-gray-900">
                      {formatCurrency(selectedTransaction.allocation?.fee || 0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Penalty</p>
                    <p className="text-base font-semibold text-gray-900">
                      {formatCurrency(selectedTransaction.allocation?.penalty || 0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Credit</p>
                    <p className="text-base font-semibold text-gray-900">
                      {formatCurrency(selectedTransaction.allocation?.credit || 0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Final Loan Balance</p>
                    <p className="text-base font-semibold text-gray-900">
                      {formatCurrency(selectedTransaction.finalLoanBalance || 0)}
                    </p>
                  </div>
                </div>
              </div>

              {selectedTransaction.notes && (
                <div className="border-t pt-4">
                  <h3 className="text-sm font-bold text-gray-900 mb-2">Notes</h3>
                  <p className="text-sm text-gray-700">{selectedTransaction.notes}</p>
                </div>
              )}

              {selectedTransaction.reversalNotes && (
                <div className="border-t pt-4">
                  <h3 className="text-sm font-bold text-red-900 mb-2">Reversal Notes</h3>
                  <p className="text-sm text-red-700">{selectedTransaction.reversalNotes}</p>
                </div>
              )}

              {selectedTransaction.createdBy && (
                <div className="border-t pt-4">
                  <p className="text-xs text-gray-500">
                    Created by: {selectedTransaction.createdBy}
                  </p>
                </div>
              )}
            </div>
            <div className="border-t p-4 flex justify-end">
              <button
                onClick={() => setSelectedTransaction(null)}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionsTab;
